import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, TextField } from '@mui/material';
import Search from '@mui/icons-material/Search';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TeamDirectoryCard } from './ClubesPageModern';
import EstadisticasEquiposPageModern from './EstadisticasEquiposPageModern';
import DirectoryPage from '../components/directories/DirectoryPage';
import DirectoryPagination from '../components/directories/DirectoryPagination';
import DirectorySavedSearches from '../components/directories/DirectoryPreferences';
import Filters from '../components/Filters';
import { useAppContext } from '../context/AppContext';
import { getDashboardGroupTeams } from '../services/dashboardV2Service';
import usePageTitle from '../hooks/usePageTitle';
import './PartidosPageModern.css';

function TeamStatisticsDirectory({ favorite }) {
  const { t, i18n } = useTranslation('directories');
  usePageTitle(t('teams'));
  const { filters, setFilters } = useAppContext();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const query = params.get('q')?.trim() || '';
  const [draft, setDraft] = useState(query);
  const initialSeason = useRef(params.get('season'));
  const { temporada, competicion, grupo } = filters;
  const hasContext = Boolean(temporada && competicion && grupo &&
    (!initialSeason.current || initialSeason.current === String(temporada)));
  const contextKey = `${temporada || ''}:${competicion || ''}:${grupo || ''}`;
  const previousContext = useRef(contextKey);
  const teams = useQuery({
    queryKey: ['directory-group-teams', temporada, competicion, grupo],
    queryFn: ({ signal }) => getDashboardGroupTeams({
      idTemporada: temporada, idCompeticion: competicion, idGrupo: grupo,
    }, signal),
    enabled: hasContext,
    staleTime: 15 * 60 * 1000,
    retry: false,
  });
  useEffect(() => { setDraft(query); }, [query]);
  useEffect(() => {
    const season = initialSeason.current;
    initialSeason.current = null;
    if (season && String(temporada) !== season) {
      setFilters({ temporada: season, federacion: '', categoria: '', competicion: '', grupo: '' });
    }
  }, [temporada, setFilters]);
  useEffect(() => {
    if (previousContext.current === contextKey) return;
    previousContext.current = contextKey;
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete('page');
      next.delete('season');
      next.delete('category');
      return next;
    }, { replace: true });
  }, [contextKey, setParams]);
  const update = (changes) => setParams((previous) => {
    const next = new URLSearchParams(previous);
    if (Object.hasOwn(changes, 'q')) next.delete('page');
    Object.entries(changes).forEach(([key, value]) => value ? next.set(key, value) : next.delete(key));
    return next;
  }, { replace: true });
  const items = (hasContext ? teams.data?.items || [] : [])
    .filter((team) => !query || String(team.nombre || '').toLocaleLowerCase(i18n.language)
      .includes(query.toLocaleLowerCase(i18n.language)))
    .slice().sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), i18n.language, { numeric: true }));
  const pageSize = 25;
  const page = Math.min(Math.max(1, Math.floor(Number(params.get('page')) || 1)), Math.max(1, Math.ceil(items.length / pageSize)));
  return <DirectoryPage className="pm-page" title={t('teamsTitle')} subtitle={t('teamsSubtitle')}
    actions={favorite && <Button variant="contained" onClick={() => navigate(`/estadisticasEquipos?${new URLSearchParams({
      id: `${favorite.idEquipo}_${favorite.idCompeticion}_${favorite.idGrupo}_${favorite.idTemporada}`,
      idTemporada: favorite.idTemporada,
    })}`, { state: { directoryFrom: location.pathname + location.search } })}>{t('myTeam')}</Button>}>
    <section className="dr-panel pm-filter-panel" aria-label={t('competitionFilters')}>
      <Filters variant="workspace" onApplyFilters={() => hasContext ? teams.refetch() : Promise.resolve()} />
    </section>
    <form className="dr-panel dr-toolbar" onSubmit={(event) => { event.preventDefault(); update({ q: draft.trim() }); }}>
      <TextField className="dr-search" label={t('searchTeam')} value={draft}
        onChange={(event) => setDraft(event.target.value)} helperText={t('filterGroupTeams')} />
      <Button type="submit" variant="contained" startIcon={<Search />}>{t('search')}</Button>
    </form>
    <DirectorySavedSearches scope="teams" context={filters} onRestore={setFilters} />
    <section className="dr-panel" aria-label={t('teams')} aria-busy={hasContext && teams.isFetching}>
      <h2>{t('teams')}</h2>
      {!hasContext && <p className="dr-empty">{t('chooseTeamGroup')}</p>}
      {hasContext && teams.isLoading && <p role="status">{t('loading')}</p>}
      {hasContext && teams.isError && <Alert severity="error"
        action={<Button onClick={() => teams.refetch()}>{t('retry')}</Button>}>{t('loadError')}</Alert>}
      {hasContext && teams.isSuccess && <p className="dr-muted">{t('resultsCount', { count: items.length })}</p>}
      {hasContext && teams.isSuccess && items.length === 0 && <p className="dr-empty">{t('noResults')}</p>}
      <div className="dr-team-list">{items.slice((page - 1) * pageSize, page * pageSize).map((team) =>
        <TeamDirectoryCard key={`${contextKey}:${team.idEquipo}`} team={{
          ...team, nombreCompeticion: team.competicionNombre || filters.competicionNombre,
        }} season={temporada} />)}</div>
      <DirectoryPagination page={page} pageSize={pageSize} total={items.length}
        onChange={(value) => update({ page: String(value) })} />
    </section>
  </DirectoryPage>;
}

export default function EquiposPageModern({ favorite }) {
  const [params] = useSearchParams();
  return params.has('id') ? <EstadisticasEquiposPageModern /> : <TeamStatisticsDirectory favorite={favorite} />;
}
